// src/pages/organization/members/ShowMember.js
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import ProfilePicture, { default as ProfileImage } from "../../../components/profileImage";
import Popup from "../../../components/popup";
import { useMainPageTitle } from "../../../contexts/PageContext";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";


const ShowMember = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine base path based on current route
  const basePath = location.pathname.startsWith('/supervisor') ? '/supervisor/members' : '/organization/members';
  
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("token");
  const { updatePageTitle } = useMainPageTitle();
  const [roles, setRoles] = useState([]);
  const [teamsOfSupervisor, setTeamsOfSupervisor] = useState([]);
  const [activeTab, setActiveTab] = useState("info");
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const fetchMemberData = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/supervisor/get-supervisor-by-id/?id=${memberId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch member data");
      const data = await response.json();
      setMember(data.data.supervisor);
      setRoles(data.data.supervisor.metaData?.roles || []);
      setTeamsOfSupervisor(data.data.teamsOfSupervisor || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [memberId, token]);

  useEffect(() => {
    fetchMemberData();
  }, [memberId, fetchMemberData]);

  useEffect(() => {
    if (member) {
      updatePageTitle(
        "Show a member",
        `${member.firstName} ${member.lastName}`
      );
    }
  }, [updatePageTitle, member]);

  // eslint-disable-next-line no-unused-vars
  const handleStatusChange = async () => {
    try {
      const response = await fetch(
        `${API_URL}/supervisor/update-status/${memberId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ isActive: !member.isActive }),
        }
      );

      if (!response.ok) throw new Error("Failed to update status");
      setMember({ ...member, isActive: !member.isActive });
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;

    try {
      const response = await fetch(`${API_URL}/supervisor/delete-supervisor/?supervisorId=${memberId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to delete member");
      setTimeout(() => navigate(basePath), 2000);
    } catch (err) {
      console.error("Error deleting member:", err);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleRoleStatusChange = async (roleId) => {
    try {
      const response = await fetch(
        `${API_URL}/supervisor/roles/${roleId}/status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            isActive: !member.roles.find((role) => role.id === roleId).isActive,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to update role status");

      setMember({
        ...member,
        roles: member.roles.map((role) =>
          role.id === roleId ? { ...role, isActive: !role.isActive } : role
        ),
      });
    } catch (err) {
      console.error("Error updating role status:", err);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleRoleDelete = async (roleId) => {
    if (!window.confirm("Are you sure you want to delete this role?")) return;

    try {
      const response = await fetch(`${API_URL}/supervisor/roles/${roleId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to delete role");

      setMember({
        ...member,
        roles: member.roles.filter((role) => role.id !== roleId),
      });
    } catch (err) {
      console.error("Error deleting role:", err);
    }
  };

  const handleEditSupervisor = () => {
    navigate(`${basePath}/create-role/${memberId}`);
  };


  const renderStatusBadge = (isActive) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      isActive 
        ? "bg-green-100 text-green-800" 
        : "bg-red-100 text-red-800"
    }`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );

  if (loading)
    return (
      <div className="flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="rounded-full bg-gray-200 h-24 w-24 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 w-full max-w-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );

  if (!member)
    return (
      <div className="flex items-center justify-center">
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 w-full max-w-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">Member not found</p>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main content */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Header with background */}
          <div className="bg-gradient-to-r from-[#F15213] to-[#F15213] px-6 py-16 relative">
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
              <div className="ring-4 ring-white rounded-full">
                <ProfilePicture
                  image={member.profilePicture}
                  alt={`${member.firstName} ${member.lastName}`}
                  className="rounded-full object-cover w-24 h-24 border-2 border-white"
                  user={{
                    firstName: member.firstName,
                    lastName: member.lastName,
                    userName: member.userName,
                    accountType: member.accountType,
                    profilePicture: member.profilePicture
                  }}
                />
              </div>
            </div>
          </div>

          {/* Profile info */}
          <div className="pt-16 px-6 pb-6 text-center">
            <div className="flex justify-center items-center space-x-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {member.firstName} {member.lastName}
              </h1>
            </div>
            <p className="text-gray-500">@{member.userName}</p>
            <p className="mt-1 text-sm text-gray-600">{member.accountType || 'supervisor'}</p>

            {/* Action buttons - always show for members page since all members are supervisors */}
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              <button
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                onClick={handleEditSupervisor}
              >
                <svg
                  className="-ml-1 mr-2 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Edit Supervisor Role
              </button>
              <Link
                to={`/chat/${member._id}`}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F15213] hover:bg-[#d14510] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F15213] transition-colors"
              >
                <svg
                  className="-ml-1 mr-2 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                    clipRule="evenodd"
                  />
                </svg>
                Chat
              </Link>
              <button
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                onClick={() => setIsConfirmDeleteOpen(true)}
              >
                <svg
                  className="-ml-1 mr-2 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                  Delete Member
                </button>
              </div>
          </div>

          {/* Tab navigation */}
          <div className="border-t border-gray-200">
            <nav className="px-6 flex space-x-4 overflow-x-auto">
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "info"
                    ? "border-[#F15213] text-[#F15213]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } transition-colors`}
                onClick={() => setActiveTab("info")}
              >
                Personal Information
              </button>
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "roles"
                    ? "border-[#F15213] text-[#F15213]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } transition-colors`}
                onClick={() => setActiveTab("roles")}
              >
                Role Permissions
              </button>
              <button
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === "teams"
                    ? "border-[#F15213] text-[#F15213]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } transition-colors`}
                onClick={() => setActiveTab("teams")}
              >
                Teams
              </button>
            </nav>
          </div>

          {/* Tab content */}
          <div className="px-6 py-5">
            {/* Personal Information Tab */}
            {activeTab === "info" && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Contact Information
                </h2>
                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Email</h3>
                    <div className="mt-1 flex items-center">
                      <svg
                        className="h-5 w-5 text-gray-400 mr-2"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                      <span className="text-gray-900 font-medium">{member.email}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Phone</h3>
                    <div className="mt-1 flex items-center">
                      <svg
                        className="h-5 w-5 text-gray-400 mr-2"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                      <span className="text-gray-900 font-medium">
                        {member.phone || "Not provided"}
                      </span>
                    </div>
                  </div>
                  
                  {/* Additional user information can be added here */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Account Type</h3>
                    <p className="mt-1 text-gray-900 font-medium">{member.accountType}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Username</h3>
                    <p className="mt-1 text-gray-900 font-medium">@{member.userName}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Role Permissions Tab */}
            {activeTab === "roles" && roles && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    Role Permissions
                  </h2>
                </div>
                <div className="bg-white shadow overflow-hidden rounded-lg">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Feature
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Create
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Read
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Update
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Delete
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(roles).map(
                          ([feature, permissions], index) => (
                            <tr
                              key={feature}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">
                                {feature}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {renderStatusBadge(permissions.create)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {permissions.read !== undefined 
                                  ? renderStatusBadge(permissions.read)
                                  : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">N/A</span>
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {renderStatusBadge(permissions.update)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {renderStatusBadge(permissions.delete)}
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Teams Tab */}
            {activeTab === "teams" && teamsOfSupervisor && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Teams Information
                </h2>
                
                {teamsOfSupervisor.length === 0 ? (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          This supervisor is not assigned to any teams.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-6">
                    {teamsOfSupervisor.map((team) => (
                      <div key={team.teamId} className="bg-white shadow overflow-hidden rounded-lg">
                        <div className="px-4 py-5 sm:px-6 flex items-center">
                          <ProfileImage
                            image={team.logo}
                            alt={team.name}
                            className="w-16 h-16 rounded-full object-cover border-2 border-gray-100 mr-4"
                          />
                          <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                              {team.teamName}
                            </h3>
                            <p className="mt-1 max-w-2xl text-sm text-gray-500">
                              @{team.uniqueName}
                            </p>
                          </div>
                        </div>
                        
                        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                          <dl className="sm:divide-y sm:divide-gray-200">
                            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                              <dt className="text-sm font-medium text-gray-500">Type</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{team.type}</dd>
                            </div>
                            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                              <dt className="text-sm font-medium text-gray-500">Gender</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{team.gender}</dd>
                            </div>
                            <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                              <dt className="text-sm font-medium text-gray-500">Sport</dt>
                              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{team.sport}</dd>
                            </div>
                          </dl>
                        </div>
                        
                        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                          <h4 className="text-md font-medium text-gray-900 mb-4">Team Role Permissions</h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feature</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Create</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Read</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Update</th>
                                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delete</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {Object.entries(team.role).map(([feature, permissions], index) => (
                                  <tr key={feature} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">{feature}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {renderStatusBadge(permissions.create)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {permissions.read !== undefined 
                                        ? renderStatusBadge(permissions.read)
                                        : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">N/A</span>
                                      }
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {renderStatusBadge(permissions.update)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {renderStatusBadge(permissions.delete)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Popup */}
      <Popup
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={handleDelete}
        message={`Are you sure you want to delete ${member?.firstName} ${member?.lastName}? This action cannot be undone.`}
      />
    </div>
  );
};

export default ShowMember;
